import * as React from 'react'

import CorporateFareOutlined from '@mui/icons-material/CorporateFareOutlined'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import { Theme, useTheme } from '@mui/material/styles'
import Typography from '@mui/material/Typography'

import { HeaderContainer } from '@user/components/DetailPages/HeaderContainer'

import { useFitsWidth } from './hooks/useFitsWidth'
import { CompanyIdentifierVariant, ICompanyIdentifier } from './typings'

interface Props {
  identifier: ICompanyIdentifier
  variant: CompanyIdentifierVariant
}

const getStyles = (theme: Theme) => ({
  row: {
    minWidth: 0,
    flex: 1,
    boxSizing: 'border-box',
    height: '40px',
    backgroundColor: theme.palette.common.white,
  },
  standalone: {
    px: '18px',
  },
  title: {
    fontFamily: 'Source Sans Pro Bold',
    fontSize: '15px',
  },
  icon: {
    flexShrink: 0,
  },
  textBox: {
    position: 'relative',
    minWidth: 0,
    flex: 1,
  },
  measurer: {
    position: 'absolute',
    visibility: 'hidden',
    whiteSpace: 'nowrap',
    pointerEvents: 'none',
  },
})

/**
 * Icon plus company text. Shows the full string when it fits; when Company
 * Name is enabled and the full string overflows, shows the name alone. Plain
 * CSS ellipsis handles the name-disabled case and a name that still overflows.
 */
export function CompanyIdentifierRow(props: Props) {
  const { identifier, variant } = props
  const styles = getStyles(useTheme())
  const { containerRef, measureRef, fits } = useFitsWidth(identifier.full)

  const text = fits || !identifier.name ? identifier.full : identifier.name

  const row = (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      data-test-id="company-identifier"
      title={identifier.full}
      sx={[
        styles.row,
        variant !== 'detail' && styles.standalone,
        variant === 'title' && styles.title,
      ]}
    >
      <CorporateFareOutlined fontSize="small" sx={styles.icon} />
      <Box ref={containerRef} sx={styles.textBox}>
        <Box component="span" ref={measureRef} aria-hidden sx={styles.measurer}>
          {identifier.full}
        </Box>
        <Typography noWrap data-test-id="company-identifier-text">
          {text}
        </Typography>
      </Box>
    </Stack>
  )

  if (variant === 'detail') {
    return (
      <HeaderContainer dataTestId="company-identifier-row" paddingY="0px">
        {row}
      </HeaderContainer>
    )
  }

  return row
}

CompanyIdentifierRow.displayName = 'CompanyIdentifierRow'
